import {
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { AntDValueSelector, QueryBuilderAntD } from '@react-querybuilder/antd'
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
  message,
} from '@surecompanies/core_components'
import dayjs from 'dayjs'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import jsonLogic from 'json-logic-js'
import Page from 'layouts/Page'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  QueryBuilder,
  type FieldSelectorProps,
  type ValueSelectorProps,
  formatQuery,
} from 'react-querybuilder'
import 'react-querybuilder/dist/query-builder.css'
import { browserHistory } from 'react-router'
import CensusOnboardingFileDropzone from '../../components/CensusOnboardingFileDropzone'
import { TEMPLATE_TYPE_LABELS } from '../../models/templateType'
import { CENSUS_ONBOARDING, ELIGIBLE_RULES_VIEW } from '../../paths'
import type { ParsedFile } from '../CreateCensusTemplate/types'
import { parseFile } from '../CreateCensusTemplate/utils/fileParser'
import { transformRows } from '../CreateCensusTemplate/utils/transformer'
import styles from './CreateEligibleRules.module.scss'
import { dateFields, fields, numberFields } from './fields'
import useActiveHrisMappingTemplates from './hooks/useActiveHrisMappingTemplates'
import useEligibleRules from './hooks/useEligibleRules'
import { ConvertedFile } from './types'

const NUMBER_FIELD_NAMES = new Set(numberFields.map((f) => f.name))
const DATE_FIELD_NAMES = new Set(dateFields.map((f) => f.name))
const PREVIEW_COLUMN_WIDTH = 180

// AntDValueSelector renders options as plain JSX <Option value={opt.name}>
// {opt.label}</Option> (not antd's `options` array API), so the label text
// only exists as children — optionFilterProp must be "children" to actually
// filter by the visible label text (there is no "label" prop to match).
function SearchableValueSelector(props: ValueSelectorProps) {
  return (
    <AntDValueSelector
      {...props}
      {...({ showSearch: true, optionFilterProp: 'children' } as any)}
    />
  )
}

function SearchableFieldSelector(props: FieldSelectorProps) {
  return (
    <AntDValueSelector
      {...(props as unknown as ValueSelectorProps)}
      {...({ showSearch: true, optionFilterProp: 'children' } as any)}
    />
  )
}

function convertFields(parsed: ParsedFile): ConvertedFile {
  const rows = parsed.rows.map((row) => {
    const converted: Record<string, string | number> = {}
    for (const [key, value] of Object.entries(row)) {
      if (NUMBER_FIELD_NAMES.has(key)) {
        converted[key] = Number(value)
      } else if (DATE_FIELD_NAMES.has(key) && !!value) {
        converted[key] = dayjs(value).format('YYYY-MM-DD')
      } else {
        converted[key] = value
      }
    }
    return converted
  })
  return { ...parsed, rows }
}

interface CreateEligibleRulesProps {
  params?: { id: string; templateId?: string }
  location?: { pathname?: string }
  route?: { path?: string }
}

export default function CreateEligibleRules({
  params,
  location,
  route,
}: CreateEligibleRulesProps) {
  const customerId = params?.id ?? ''
  const templateId = params?.templateId

  const mode: 'create' | 'edit' | 'view' = (() => {
    const routePath = route?.path ?? ''
    const urlPath = location?.pathname ?? ''
    if (
      routePath.includes(ELIGIBLE_RULES_VIEW) ||
      urlPath.includes(ELIGIBLE_RULES_VIEW)
    ) {
      return 'view'
    }
    if (templateId) return 'edit'
    return 'create'
  })()

  const isViewOnly = mode === 'view'

  const {
    query,
    setQuery,
    saveDraft,
    saveAndPublish,
    saveEditDraft,
    publishEdit,
    resetRules,
    isSaving,
    templateName,
    templateDescription,
    isLoadingTemplate,
  } = useEligibleRules(customerId, templateId)

  const [parsedFile, setParsedFile] = useState<ConvertedFile | null>(null)
  const [uploadSource, setUploadSource] = useState<'census' | 'hris' | null>(
    null
  )
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveModalAction, setSaveModalAction] = useState<'DRAFT' | 'PUBLISHED'>(
    'DRAFT'
  )
  const [form] = Form.useForm()

  const { activeTemplates, isLoading: isLoadingHrisTemplates } =
    useActiveHrisMappingTemplates(customerId)

  const [selectedHrisTemplateId, setSelectedHrisTemplateId] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    if (activeTemplates.length === 0) {
      setSelectedHrisTemplateId(undefined)
      return
    }
    setSelectedHrisTemplateId((current) => {
      if (current && activeTemplates.some((t) => t.id === current)) {
        return current
      }
      return activeTemplates[0].id
    })
  }, [activeTemplates])

  const selectedHrisTemplate = useMemo(
    () => activeTemplates.find((t) => t.id === selectedHrisTemplateId),
    [activeTemplates, selectedHrisTemplateId]
  )

  // Switching the mapping template invalidates any already-uploaded/mapped
  // data (it was mapped with the previous template) — clear it so the user
  // uploads fresh rather than silently viewing stale results.
  const handleHrisTemplateChange = useCallback((newTemplateId: string) => {
    setSelectedHrisTemplateId(newTemplateId)
    setParsedFile(null)
    setUploadSource(null)
  }, [])

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      const result = await parseFile(file)
      const converted = convertFields(result)
      setParsedFile(converted)
      setUploadSource('census')
      message.success(
        `Loaded ${result.recordCount} records: ${result.columnCount} columns found`
      )
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to parse file')
    }
  }, [])

  const handleHrisFileUpload = useCallback(
    async (file: File) => {
      if (!selectedHrisTemplate) {
        message.error(
          'No active HRIS mapping template is available for this company.'
        )
        return
      }
      try {
        const result = await parseFile(file)
        const transformedRows = transformRows(
          selectedHrisTemplate.mappingRules,
          result.rows,
          selectedHrisTemplate.computedFields
        )
        const mappedColumns = Array.from(
          new Set(selectedHrisTemplate.mappingRules.map((rule) => rule.target))
        )
        const converted = convertFields({
          ...result,
          rows: transformedRows,
          columns: mappedColumns,
          columnCount: mappedColumns.length,
        })
        setParsedFile(converted)
        setUploadSource('hris')
        message.success(
          `Loaded ${result.recordCount} records: mapped to ${converted.columns.length} census fields using the ${TEMPLATE_TYPE_LABELS[selectedHrisTemplate.type]} template — ${selectedHrisTemplate.name}`
        )
      } catch (err) {
        message.error(
          err instanceof Error ? err.message : 'Failed to parse file'
        )
      }
    },
    [selectedHrisTemplate]
  )

  const openSaveModal = useCallback(
    (action: 'DRAFT' | 'PUBLISHED') => {
      if (query.rules.length === 0) {
        message.warning('Add at least one rule before saving.')
        return
      }
      setSaveModalAction(action)
      form.resetFields()
      setSaveModalOpen(true)
    },
    [query.rules.length, form]
  )

  const handleSaveDraft = useCallback(
    () => openSaveModal('DRAFT'),
    [openSaveModal]
  )

  const handlePublish = useCallback(
    () => openSaveModal('PUBLISHED'),
    [openSaveModal]
  )

  const handleSaveConfirm = useCallback(async () => {
    try {
      const values = await form.validateFields()
      const params = {
        templateName: values.templateName,
        description: values.description,
      }
      if (mode === 'create') {
        if (saveModalAction === 'PUBLISHED') {
          await saveAndPublish(params)
        } else {
          await saveDraft(params)
        }
      } else if (mode === 'edit') {
        if (saveModalAction === 'PUBLISHED') {
          await publishEdit(params)
        } else {
          await saveEditDraft(params)
        }
      }
      setSaveModalOpen(false)
      form.resetFields()
      browserHistory.push(`/${CENSUS_ONBOARDING}/${customerId}`)
    } catch {
      // validation errors are shown inline; API errors are shown via message in the hook
    }
  }, [
    form,
    mode,
    saveModalAction,
    saveAndPublish,
    saveDraft,
    saveEditDraft,
    publishEdit,
    customerId,
  ])

  const handleSaveCancel = useCallback(() => {
    setSaveModalOpen(false)
    form.resetFields()
  }, [form])

  const handleReset = useCallback(() => {
    resetRules()
    setParsedFile(null)
    setUploadSource(null)
  }, [resetRules])

  const csvColumns = useMemo(
    () =>
      (parsedFile?.columns ?? []).map((col) => ({
        title: col,
        dataIndex: col,
        key: col,
        ellipsis: true,
        width: PREVIEW_COLUMN_WIDTH,
      })),
    [parsedFile]
  )

  // scroll.x must be a concrete number (not `true`) for antd to honor each
  // column's fixed width instead of auto-shrinking them to fit the container.
  const previewScrollX = csvColumns.length * PREVIEW_COLUMN_WIDTH

  const { eligibleRows, ineligibleRows } = useMemo(() => {
    if (!parsedFile || query.rules.length === 0)
      return { eligibleRows: [], ineligibleRows: [] }
    const logic = formatQuery(query, 'jsonlogic')
    const eligible: Record<string, string | number>[] = []
    const ineligible: Record<string, string | number>[] = []
    for (const row of parsedFile.rows) {
      jsonLogic.apply(logic, row) ? eligible.push(row) : ineligible.push(row)
    }
    return { eligibleRows: eligible, ineligibleRows: ineligible }
  }, [parsedFile, query])

  const naturalLanguageQuery = useMemo(
    () =>
      query.rules.length > 0 ? formatQuery(query, 'natural_language') : '',
    [query]
  )

  const showPreview = parsedFile !== null && query.rules.length > 0

  const breadcrumbTitle =
    mode === 'view'
      ? 'View Eligible Rules'
      : mode === 'edit'
        ? 'Edit Eligible Rules'
        : 'Create Eligible Rules'

  if (isLoadingTemplate && templateId) {
    return (
      <Page title="">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin spinning />
        </div>
      </Page>
    )
  }

  return (
    <Page
      title=""
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: '/' },
            { title: 'Census Onboarding', path: `/${CENSUS_ONBOARDING}` },
            {
              title: 'Customer Detail',
              path: `/${CENSUS_ONBOARDING}/${customerId}`,
            },
            { title: breadcrumbTitle },
          ]}
        />
      }
    >
      {/* Page header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerTitle}>
          <div className={styles.headerIcon}>
            <BarChartOutlined />
          </div>
          <div className={styles.headerInfo}>
            <h2>
              {mode === 'view'
                ? 'View Eligible Rules'
                : mode === 'edit'
                  ? 'Edit Eligible Rules'
                  : 'Eligible Rules'}
            </h2>
            <p>Build eligibility conditions using census fields</p>
          </div>
        </div>

        {!isViewOnly && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              className={styles.resetLink}
              onClick={handleReset}
              role="button"
              tabIndex={0}
            >
              Reset
            </span>
            {(mode === 'create' || mode === 'edit') && (
              <Button
                icon={<SaveOutlined />}
                onClick={handleSaveDraft}
                loading={isSaving}
                disabled={query.rules.length === 0}
              >
                Save Draft
              </Button>
            )}
            {(mode === 'create' || mode === 'edit') && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handlePublish}
                loading={isSaving}
                disabled={query.rules.length === 0}
              >
                Save & Publish
              </Button>
            )}
          </div>
        )}

        {isViewOnly && (
          <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px' }}>
            View Only
          </Tag>
        )}
      </div>

      {/* Section 01: Upload File */}
      <Card className={styles.ruleBuilderCard}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>01</span>
          <span className={styles.sectionTitle}>Upload File</span>
        </div>
        <p className={styles.sectionSubtitle}>
          Upload a census file, or an HRIS file mapped automatically using this
          company&apos;s active mapping template, to preview which rows are
          eligible or ineligible based on the rules below.
        </p>

        <div className={styles.uploadRow}>
          <div className={styles.uploadColumn}>
            <label className={styles.uploadColumnLabel}>
              Upload Census File
            </label>
            {/* Keeps the dropzones aligned when the HRIS template Select
                renders on the other column. */}
            {activeTemplates.length > 1 && (
              <div className={styles.uploadSelectSpacer} />
            )}
            <CensusOnboardingFileDropzone
              parsedFile={uploadSource === 'census' ? parsedFile : null}
              onFileSelected={handleFileUpload}
              emptyHint="CSV, XLS, and XLSX accepted"
            />
          </div>

          <div className={styles.uploadColumn}>
            <label className={styles.uploadColumnLabel}>Upload HRIS File</label>
            {activeTemplates.length > 1 && (
              <Select
                style={{ width: '100%', marginBottom: 8 }}
                value={selectedHrisTemplateId}
                onChange={handleHrisTemplateChange}
                options={activeTemplates.map((template) => ({
                  value: template.id,
                  label: `${TEMPLATE_TYPE_LABELS[template.type]} — ${template.name}`,
                }))}
              />
            )}
            <CensusOnboardingFileDropzone
              parsedFile={uploadSource === 'hris' ? parsedFile : null}
              onFileSelected={handleHrisFileUpload}
              emptyHint={
                isLoadingHrisTemplates
                  ? 'Checking for an active HRIS mapping template…'
                  : selectedHrisTemplate
                    ? `Mapped automatically using the ${TEMPLATE_TYPE_LABELS[selectedHrisTemplate.type]} template — ${selectedHrisTemplate.name}`
                    : 'No active HRIS mapping template found for this company'
              }
              disabled={!selectedHrisTemplate}
            />
          </div>
        </div>
      </Card>

      {/* Section 02: Rule Builder */}
      <Card className={styles.ruleBuilderCard}>
        <div className={styles.ruleBuilderHeader}>
          <div className={styles.ruleBuilderLeft}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionBadge}>02</span>
              <span className={styles.sectionTitle}>Rule Builder</span>
            </div>
          </div>
        </div>
        <p className={styles.sectionSubtitle}>
          Define eligibility conditions by combining rules. Use AND / OR to
          group conditions.
        </p>

        <div
          className={styles.queryBuilderWrapper}
          id={isViewOnly ? 'eligible-rules-view-only' : undefined}
        >
          <QueryBuilderAntD>
            <QueryBuilder
              fields={fields}
              query={query}
              onQueryChange={isViewOnly ? undefined : setQuery}
              disabled={isViewOnly}
              controlElements={{
                valueSelector: SearchableValueSelector,
                fieldSelector: SearchableFieldSelector,
              }}
            />
          </QueryBuilderAntD>
        </div>

        {naturalLanguageQuery && (
          <div className={styles.naturalLanguageBlock}>
            <span className={styles.naturalLanguageLabel}>Rule summary</span>
            <p className={styles.naturalLanguageText}>{naturalLanguageQuery}</p>
          </div>
        )}
      </Card>

      {/* Section 03: Preview */}
      <Card className={styles.previewCard}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>03</span>
          <span className={styles.sectionTitle}>Preview Results</span>
        </div>

        {showPreview ? (
          <>
            <p className={styles.sectionSubtitle}>
              All rows evaluated against the current rules.
            </p>

            <div className={styles.previewTables}>
              {/* Eligible table */}
              <div className={styles.previewTableWrapper}>
                <div className={styles.previewLabelEligible}>
                  <CheckCircleOutlined />
                  <span>Eligible</span>
                  <Tag color="green">{eligibleRows.length}</Tag>
                </div>
                <Table
                  dataSource={eligibleRows.map((r, i) => ({ ...r, _key: i }))}
                  columns={csvColumns as any}
                  rowKey="_key"
                  pagination={false}
                  size="small"
                  scroll={{ x: previewScrollX, y: 600 }}
                  locale={{ emptyText: 'No eligible rows' }}
                />
              </div>

              <div className={styles.previewDivider} />

              {/* Ineligible table */}
              <div className={styles.previewTableWrapper}>
                <div className={styles.previewLabelIneligible}>
                  <CloseCircleOutlined />
                  <span>Ineligible</span>
                  <Tag color="red">{ineligibleRows.length}</Tag>
                </div>
                <Table
                  dataSource={ineligibleRows.map((r, i) => ({
                    ...r,
                    _key: i,
                  }))}
                  columns={csvColumns as any}
                  rowKey="_key"
                  pagination={false}
                  size="small"
                  scroll={{ x: previewScrollX, y: 600 }}
                  locale={{ emptyText: 'No ineligible rows' }}
                />
              </div>
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>
            {!parsedFile
              ? 'Upload a census file and add rules to see the preview.'
              : 'Add at least one rule to see the preview.'}
          </div>
        )}
      </Card>

      {/* Save Modal (create mode only) */}
      <Modal
        title={
          saveModalAction === 'PUBLISHED'
            ? 'Save & Publish Template'
            : 'Save Draft'
        }
        open={saveModalOpen}
        onOk={handleSaveConfirm}
        onCancel={handleSaveCancel}
        okText={
          saveModalAction === 'PUBLISHED' ? 'Save & Publish' : 'Save Draft'
        }
        okButtonProps={{
          loading: isSaving,
          danger: saveModalAction === 'PUBLISHED',
        }}
        cancelText="Cancel"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="templateName"
            label="Template Name"
            initialValue={templateName}
            rules={[
              { required: true, message: 'Please enter a template name' },
            ]}
          >
            <Input
              placeholder="e.g. Standard Full-Time Eligibility"
              maxLength={255}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description (optional)"
            initialValue={templateDescription}
          >
            <Input.TextArea
              placeholder="Describe the purpose of this eligibility rule..."
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </Page>
  )
}
